import { useState, useEffect, useCallback } from "react";
import { Building2, AlertTriangle, CheckCircle2, XCircle, Loader2, UserCheck, ExternalLink, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TierBadge } from "@/components/TierBadge";
import { getCriticita, updateVerificaAzienda } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const STATI = [["aperta", "Aperte"], ["in_verifica", "In verifica"], ["fattibile", "Fattibili"], ["non_fattibile", "Non fattibili"]];
const TONE = {
  aperta: "bg-amber-400/15 text-amber-300 border-amber-400/40",
  in_verifica: "bg-sky-400/15 text-sky-300 border-sky-400/40",
  fattibile: "bg-emerald-400/15 text-emerald-300 border-emerald-400/40",
  non_fattibile: "bg-rose-400/15 text-rose-300 border-rose-400/40",
};
const fmtEur = (n) => n ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : "—";

function Card({ c, consulenti, isAdmin, me, onOpen, onChanged }) {
  const v = c.verifica || { stato: "aperta" };
  const [note, setNote] = useState(v.note || "");
  const [busy, setBusy] = useState(false);
  const canDecide = isAdmin || v.assegnato_a === me;
  const closed = v.stato === "fattibile" || v.stato === "non_fattibile";

  const act = async (payload, msg) => {
    setBusy(true);
    try { await updateVerificaAzienda(c.id, payload); toast.success(msg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`criticita-card-${c.id}`} className="rounded-xl bg-slate-900 border border-white/10 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold text-white">{c.nome || "Senza nome"}</span>
            <TierBadge tier={c.tier} />
            <span data-testid={`criticita-stato-${c.id}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${TONE[v.stato]}`}>{STATI.find((s) => s[0] === v.stato)?.[1]}</span>
          </div>
          <div className="text-xs text-slate-400 mt-0.5 truncate">{c.telefono} · {fmtEur(c.importo)} · {c.stage}</div>
          <div className="text-sm text-slate-200 mt-1 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-amber-400 shrink-0" /><span className="font-medium">{c.azienda || "Azienda n.d."}</span>
            {c.rischio && <span className={`text-xs ${c.rischio === "alto" ? "text-rose-400" : "text-slate-400"}`}>· rischio {c.rischio}</span>}</div>
        </div>
        <Button data-testid={`criticita-open-${c.id}`} size="sm" variant="ghost" onClick={() => onOpen(c.id)} className="text-slate-300 gap-1 shrink-0"><ExternalLink className="h-4 w-4" /><span className="hidden sm:inline">Lead</span></Button>
      </div>

      <ul className="rounded-md border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-100 space-y-1" data-testid={`criticita-lista-${c.id}`}>
        {c.criticita.map((k) => <li key={k} className="flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-400" />{k}</li>)}
        {c.cassa_integrazione && c.cassa_integrazione !== "n.d." && <li className="text-amber-200/80">Cassa integrazione: {c.cassa_integrazione}</li>}
        {c.procedure && !/nessun/i.test(c.procedure) && <li className="text-amber-200/80">Procedure: {c.procedure}</li>}
      </ul>
      {c.sintesi_web && <p className="text-xs text-sky-200">{c.sintesi_web}</p>}

      <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-slate-400"><UserCheck className="h-4 w-4" />
            {isAdmin ? (
              <Select value={v.assegnato_a || "none"} onValueChange={(val) => act({ assegnato_a: val === "none" ? "" : val }, val === "none" ? "Assegnazione rimossa" : "Consulente assegnato")} disabled={busy || closed}>
                <SelectTrigger data-testid={`criticita-assegna-${c.id}`} className="h-8 w-full sm:w-64 bg-slate-800 border-white/10 text-slate-200 text-xs"><SelectValue placeholder="Assegna consulente" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10 text-slate-100">
                  <SelectItem value="none">Nessun consulente</SelectItem>
                  {consulenti.map((u) => <SelectItem key={u.email} value={u.email}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : <span>Assegnata a: <span className="text-slate-200">{v.assegnato_nome || "—"}</span></span>}
          </div>
          <div className="flex gap-2">
            <Textarea data-testid={`criticita-note-${c.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Esito della verifica con l'azienda / il cliente…" className="bg-slate-800 border-white/10 text-sm min-h-[60px]" disabled={busy} />
            <Button data-testid={`criticita-salva-note-${c.id}`} size="icon" variant="ghost" onClick={() => act({ note }, "Note salvate")} disabled={busy || note === (v.note || "")} className="text-slate-300 shrink-0"><Save className="h-4 w-4" /></Button>
          </div>
          {closed && <div className="text-[11px] text-slate-500">Decisa da {v.decisa_da} · {v.decisa_il?.slice(0, 10)}</div>}
        </div>
        {!closed && (
          <div className="flex sm:flex-col gap-2">
            <Button data-testid={`criticita-fattibile-${c.id}`} size="sm" disabled={busy || !canDecide} onClick={() => act({ stato: "fattibile", note }, "Segnata come fattibile: il lead torna in pipeline")} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 flex-1"><CheckCircle2 className="h-4 w-4" /> Fattibile</Button>
            <Button data-testid={`criticita-nonfattibile-${c.id}`} size="sm" disabled={busy || !canDecide} onClick={() => act({ stato: "non_fattibile", note }, "Segnata come non fattibile")} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-1 flex-1"><XCircle className="h-4 w-4" /> Non fattibile</Button>
          </div>
        )}
        {!canDecide && !closed && <p className="text-[11px] text-slate-500 sm:col-span-2">Solo il consulente assegnato o l'amministratore può decidere.</p>}
      </div>
    </div>
  );
}

export default function CriticitaPanel({ onOpenLead, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("");
  const refresh = useCallback(() => getCriticita(filtro).then(setData).catch(() => toast.error("Impossibile caricare le criticità")), [filtro]);
  useEffect(() => { refresh(); }, [refresh]);
  const changed = () => { refresh(); onChanged?.(); };

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const consulenti = (data.consulenti || []).filter((u) => u.email);

  return (
    <div data-testid="criticita-panel" className="space-y-5">
      <div className="rounded-xl bg-amber-400/5 border border-amber-400/20 p-4 text-sm text-amber-100/90">
        <div className="font-display font-semibold text-white flex items-center gap-2"><Building2 className="h-4 w-4 text-amber-400" /> Criticità aziendali</div>
        Lead la cui azienda presenta problemi (crisi, cassa integrazione, procedure, perdite). Nulla viene scartato: l'amministratore assegna un consulente che verifica la fattibilità e decide.
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-3 px-3 sm:mx-0 sm:px-0">
        <button data-testid="criticita-filtro-tutte" onClick={() => setFiltro("")} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${!filtro ? "bg-white text-slate-900 border-white" : "border-white/15 text-slate-300"}`}>Tutte</button>
        {STATI.map(([k, l]) => (
          <button key={k} data-testid={`criticita-filtro-${k}`} onClick={() => setFiltro(k)} className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full border ${filtro === k ? "bg-white text-slate-900 border-white" : "border-white/15 text-slate-300"}`}>{l} <span className="opacity-60">{data.conteggi?.[k] ?? 0}</span></button>
        ))}
      </div>
      {data.items.length === 0 ? (
        <div className="py-12 text-center text-slate-500 text-sm" data-testid="criticita-empty">Nessuna criticità {filtro ? "in questo stato" : "aperta"}: tutte le aziende dei lead risultano regolari.</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {data.items.map((c) => <Card key={c.id + (c.verifica?.updated_at || "")} c={c} consulenti={consulenti} isAdmin={user?.role === "admin"} me={user?.email} onOpen={onOpenLead} onChanged={changed} />)}
        </div>
      )}
    </div>
  );
}
